// client/src/components/dashboard/account/ProfileForm.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaSave } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner';
import nationalitiesList from '../../../utils/nationalities'; // Assuming this file exists and exports an array
import languagesList from '../../../utils/languages';       // Assuming this file exists and exports an array
// --- Import API functions ---
import { fetchMyProfile, updateMyProfile } from '../../../api/authApi'; // Adjust path if needed
// Removed direct axios import

import './ProfileForm.css';

// Initial empty form data structure
const initialFormData = {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    genderIdentity: '',
    nationality: '',
    primaryLanguage: '',
};

// Helper function to format date strings to yyyy-MM-dd
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return ''; // Handle invalid date strings
        return date.toISOString().split('T')[0]; // Extracts yyyy-MM-dd part
    } catch (e) { return ''; }
};
// --- End Helper ---

function ProfileForm() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // --- State managed within ProfileForm ---
    const [profileData, setProfileData] = useState(null); // Stores the last saved profile
    const [formData, setFormData] = useState(initialFormData); // Data currently in the form fields
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true); // Loading for initial fetch or saving
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    // --- Effect for fetching initial profile data ---
    useEffect(() => {
        const loadProfile = async () => {
            setLoading(true);
            setError(null);
            setMessage('');
            try {
                // Use the API function (handles auth via cookie)
                const data = await fetchMyProfile();
                if (data) {
                    setProfileData(data); // Store the fetched profile
                    // Populate form state (assuming API returns snake_case)
                    setFormData({
                        firstName: data.first_name || '',
                        lastName: data.last_name || '',
                        dateOfBirth: formatDateForInput(data.date_of_birth), // Format for input
                        genderIdentity: data.gender_identity || '',
                        nationality: data.nationality || '',
                        primaryLanguage: data.primary_language || '',
                    });
                } else {
                     // Profile might not exist yet (404 handled by API function)
                     setProfileData(null);
                     setFormData(initialFormData);
                     console.log("Profile not found, initializing empty form.");
                }
            } catch (err) {
                // Errors (like 401) caught/processed by handleApiError in authApi.js
                 console.error("Error loading profile:", err);
                 // Check specifically for 404 if handleApiError doesn't make it obvious
                 if (err.message.includes('404') || err.response?.status === 404) {
                      console.log("Profile not found (404), initializing empty form.");
                      setProfileData(null);
                      setFormData(initialFormData);
                      // Optionally clear the error state if 404 is expected
                      // setError(null);
                 } else {
                      setError(err.message || t('errors.api.requestFailed')); // Show translated error
                 }
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
    }, [t]); // Dependency: t for translations in errors

    // --- Input Change Handler ---
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
        // Clear errors/messages when user modifies data
        if (error) setError(null);
        if (message) setMessage('');
    };

    // --- Edit Mode Toggle Handler ---
    const toggleEditMode = () => {
         const currentlyEditing = !editMode;
         setEditMode(currentlyEditing);
         setError(null); // Clear errors on mode toggle
         setMessage(''); // Clear messages on mode toggle

         // If exiting edit mode (cancelling), reset form to last saved state
         if (!currentlyEditing) {
             if (profileData) {
                 // Reset from saved profileData (snake_case keys)
                 setFormData({
                    firstName: profileData.first_name || '',
                    lastName: profileData.last_name || '',
                    dateOfBirth: formatDateForInput(profileData.date_of_birth),
                    genderIdentity: profileData.gender_identity || '',
                    nationality: profileData.nationality || '',
                    primaryLanguage: profileData.primary_language || '',
                 });
             } else {
                 // Reset to empty if no profile existed initially
                 setFormData(initialFormData);
             }
         }
     };

    // --- Form Submit Handler ---
    const handleSubmit = async (event) => {
        if(event) event.preventDefault();
        setError(null); setMessage('');

        // --- Validation ---
        // Fields are optional, only validate format if value exists
        if (formData.dateOfBirth) {
             const dob = new Date(formData.dateOfBirth);
             if (isNaN(dob.getTime())) { setError(t('account.errors.invalidDate')); return; }
             const today = new Date(); today.setHours(0, 0, 0, 0);
             if (dob > today) { setError(t('account.errors.futureDate')); return; }
         }
         if (formData.primaryLanguage && !/^[a-zA-Z\s-]+$/.test(formData.primaryLanguage)) {
             setError(t('account.errors.invalidLanguage')); return;
         }
        // --- End Validation ---

        setLoading(true);
        try {
            // Prepare payload with snake_case keys for the backend
            const payload = {
                first_name: formData.firstName.trim() || null,
                last_name: formData.lastName.trim() || null,
                date_of_birth: formatDateForInput(formData.dateOfBirth),
                gender_identity: formData.genderIdentity || null,
                nationality: formData.nationality || null,
                primary_language: formData.primaryLanguage || null,
            };
            console.log("Attempting to update profile with payload:", payload);

            // Use the API function from authApi.js
            const updatedProfile = await updateMyProfile(payload);

            console.log("Profile update successful:", updatedProfile);
            setMessage(t('dashboard.account.profile.updateSuccess')); // Show success message
            setProfileData(updatedProfile); // Store the newly saved profile data
            // Update form data state with the response (snake_case keys)
            setFormData({
               firstName: updatedProfile.first_name || '',
               lastName: updatedProfile.last_name || '',
               dateOfBirth: formatDateForInput(updatedProfile.date_of_birth),
               genderIdentity: updatedProfile.gender_identity || '',
               nationality: updatedProfile.nationality || '',
               primaryLanguage: updatedProfile.primary_language || '',
            });
            setEditMode(false); // Exit edit mode
            setError(null); // Clear any previous error

        } catch (err) {
            // Errors caught/processed by handleApiError in authApi.js
            console.error("Error updating profile:", err);
            setError(err.message || t('errors.api.requestFailed')); // Show translated error
            // Remain in edit mode on error
        } finally {
            setLoading(false); // Stop loading indicator
        }
        // No redirect - user stays on the page
    };

    // Show loading spinner only during the initial data fetch
    if (loading && !profileData && !error && !message) {
        return <LoadingSpinner />;
    }

    // --- Render Form ---
    return (
        <form onSubmit={handleSubmit} className="account-form">
            {/* Display success/error messages */}
             {/* Display success/error messages */}
             {message && <div className="form-success">{message}</div>}
             {error && <div className="form-error">{error}</div>}

            {/* Form Rows with Inputs/Selects */}
            <div className="form-row">
                 {/* First Name Input */}
                <div className="form-group">
                    <label htmlFor="firstName">{t('dashboard.account.profile.firstName')}:</label>
                    <input
                        type="text" id="firstName" name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                    />
                </div>
                 {/* Last Name Input */}
                <div className="form-group">
                    <label htmlFor="lastName">{t('dashboard.account.profile.lastName')}:</label>
                    <input
                        type="text" id="lastName" name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                    />
                </div>
                 {/* Date of Birth Input */}
                <div className="form-group">
                    <label htmlFor="dateOfBirth">{t('dashboard.account.profile.dateOfBirth')}:</label>
                    <input
                        type="date" id="dateOfBirth" name="dateOfBirth"
                        value={formData.dateOfBirth} // Format YYYY-MM-DD expected by input type=date
                        onChange={handleInputChange}
                        disabled={!editMode}
                    />
                </div>
            </div>
            <div className="form-row">
                 {/* Gender Identity Select */}
                <div className="form-group">
                     <label htmlFor="genderIdentity">{t('dashboard.account.profile.genderIdentity')}:</label>
                     <select
                        id="genderIdentity" name="genderIdentity"
                        value={formData.genderIdentity || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                     >
                         <option value="" >{t('dashboard.account.profile.selectGender')}</option>
                         <option value="male">{t('dashboard.account.profile.male')}</option>
                         <option value="female">{t('dashboard.account.profile.female')}</option>
                         <option value="other">{t('dashboard.account.profile.other')}</option>
                     </select>
                 </div>
                 {/* Nationality Select */}
                 <div className="form-group">
                      <label htmlFor="nationality">{t('dashboard.account.profile.nationality')}:</label>
                      <select
                         id="nationality" name="nationality"
                         value={formData.nationality || ''}
                         onChange={handleInputChange}
                         disabled={!editMode}
                      >
                         <option value="">{t('dashboard.account.profile.selectNationality')}</option>
                         {nationalitiesList.map(nat => (
                            <option key={nat.value} value={nat.value}>{t(`nationalities.${nat.value}`)}</option>
                         ))}
                      </select>
                 </div>
                  {/* Primary Language Select */}
                  <div className="form-group">
                      <label htmlFor="primaryLanguage">{t('dashboard.account.profile.primaryLanguage')}:</label>
                      <select
                         id="primaryLanguage" name="primaryLanguage"
                         value={formData.primaryLanguage || ''}
                         onChange={handleInputChange}
                         disabled={!editMode}
                      >
                         <option value="">{t('dashboard.account.profile.selectLanguage')}</option>
                         {languagesList.map((lang) => (
                            <option key={lang.value} value={lang.value}>{t(`languages.${lang.value}`)}</option>
                         ))}
                      </select>
                  </div>
            </div>

            {/* Button group */}
            <div className={`button-group ${isRTL ? 'rtl' : ''}`}>
                <button
                    type="button" // Use type="button" to prevent default form submit when only toggling
                    onClick={editMode ? handleSubmit : toggleEditMode} // Call appropriate handler
                    className={editMode ? 'save-button' : 'edit-button'}
                    disabled={loading} // Disable while loading/saving
                >
                    {editMode ? (
                         <>{loading ? t('dashboard.account.profile.updating') : <><FaSave /> {t('dashboard.account.profile.update')}</>}</>
                    ) : (
                         <><FaEdit /> {t('dashboard.account.profile.edit')}</>
                    )}
                </button>
                {/* Cancel button removed */}
            </div>
        </form>
    );
}

export default ProfileForm;